"use client";

import React, { useState } from "react";
import { Check, X, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pricingTiers = [{
  name: "基本日常生活照護",
  description: "專為需要基本日常生活協助的服務使用者設計，提供溫馨的陪伴與照顧。",
  monthlyPrice: 5800,
  yearlyPrice: 15660,
  features: ["血糖、生命表徵監察", "健康問題諮詢、心靈支持及輔導", "基本個人衛生護理（每星期3次 ）", "上門護士執藥及用藥教導", "陪診服務 (每月1次)"],
  notIncluded: ["專業傷口護理", "物理/職業治療", "24小時緊急支援"],
  highlighted: false,
  cta: "了解方案詳情"
}, {
  name: "專業復康護理",
  description: "最受歡迎的計劃，結合專業護理與復康治療，提升生活質素。",
  monthlyPrice: 10500,
  yearlyPrice: 28350,
  features: ["資深護士定期評估", "基本個人衛生護理（每星期3次 ）", "專業傷口及導管護理", "個人化復康運動計劃", "上門護士執藥及用藥教導", "陪診服務 (每月2次)"],
  notIncluded: ["24小時駐場護理"],
  highlighted: true,
  badge: "最受推薦",
  cta: "立即預約評估"
}, {
  name: "全方位醫護支援",
  description: "為有高度醫療需求或長期病患家庭提供全天候的專業醫護保障。",
  monthlyPrice: 17500,
  yearlyPrice: 47250,
  features: ["跨專業團隊綜合護理", "基本個人衛生護理（每星期5次 ）", "營養師及言語治療師評估", "上門護士執藥、專業傷口及導管護理", "高級醫療設備租賃支援", "24/7 專屬醫護支援團隊"],
  notIncluded: [],
  highlighted: false,
  cta: "聯絡專業顧問"
}];
export default function ChooseYourSeasonFlatrate() {
  const [isYearly, setIsYearly] = useState(false);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="97512" id="護理計劃資費" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[1000px] h-[600px] bg-primary/5 blur-[150px] rounded-[100%] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-6">
            <Leaf className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6 tracking-tight text-primary">
            選擇最適合您的護理計劃
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl mb-10">
            我們提供透明且具彈性的收費準則，旨在為香港家庭提供最專業、最有尊嚴的居家醫護支援。每份計劃均可根據實際需要彈性調整。
          </p>

          {}
          <div className="flex items-center justify-center gap-4 bg-card/50 backdrop-blur-sm border border-border p-2 rounded-full w-fit mx-auto shadow-sm">
            <span className={`text-sm px-4 py-2 rounded-full transition-colors ${!isYearly ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"}`}>
              按月支付
            </span>
            <Switch checked={isYearly} onCheckedChange={setIsYearly} className="data-[state=checked]:bg-primary" />
            <span className={`text-sm px-4 py-2 rounded-full transition-colors flex items-center gap-2 ${isYearly ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"}`}>
              季度套票
              <Badge variant="secondary" className="bg-accent text-accent-foreground hover:bg-accent/80 border-none">
                9 折優惠
              </Badge>
            </span>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-center">
          {pricingTiers.map((tier, i) => <div key={tier.name} data-index={i} className={`relative rounded-3xl p-8 flex flex-col h-full transition-all duration-1000 delay-[${i * 150}ms] ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${tier.highlighted ? "bg-card border-2 border-primary shadow-[0_10px_40px_-10px_rgba(107,33,168,0.15)] md:-translate-y-4 z-10" : "bg-card/40 backdrop-blur-md border border-border hover:bg-card/60"}`}>
              {tier.highlighted && <Badge className="absolute top-0 right-8 -translate-y-1/2 bg-primary text-primary-foreground hover:bg-primary/90 px-3 py-1 text-sm uppercase tracking-wider font-bold border-none">
                  {tier.badge}
                </Badge>}

              <div className="mb-8">
                <h3 className="text-2xl font-heading font-semibold mb-2 !text-[hsl(263,69%,42%)]">
                  {tier.name}
                </h3>
                <p className="text-muted-foreground text-sm min-h-[40px]">
                  {tier.description}
                </p>
              </div>

              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold tracking-tighter text-foreground">
                    HK${isYearly ? tier.yearlyPrice.toLocaleString() : tier.monthlyPrice.toLocaleString()}
                  </span>
                  <span className="text-muted-foreground">
                    /{isYearly ? "季" : "月"}
                  </span>
                </div>
                {isYearly && <p className="text-sm text-primary mt-2 font-medium">
                    每季結算 HK${tier.yearlyPrice.toLocaleString()}
                  </p>}
              </div>

              <div className="flex-grow space-y-6 mb-8">
                <ul className="space-y-3">
                  {tier.features.map((feature, index) => <li key={index} className="flex items-start gap-3 text-sm">
                      <Check className="w-5 h-5 text-primary shrink-0" />
                      <span className="text-foreground/90">{feature}</span>
                    </li>)}
                </ul>
                
                {tier.notIncluded.length > 0 && <ul className="space-y-3 pt-4 border-t border-border/50">
                    {tier.notIncluded.map((feature, index) => <li key={index} className="flex items-start gap-3 text-sm opacity-50">
                        <X className="w-5 h-5 text-muted-foreground shrink-0" />
                        <span>{feature}</span>
                      </li>)}
                  </ul>}
              </div>

              <Button variant={tier.highlighted ? "default" : "outline"} size="lg" className={`w-full rounded-xl ${tier.highlighted ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20" : "border-border hover:bg-secondary hover:text-secondary-foreground"}`}>
                {tier.cta}
              </Button>
            </div>)}
        </div>
      </div>
    </section>;
}